// Applies the user's saved drag-reorder to a spots-by-id map, since object keys that
// look numeric get silently re-sorted ascending by JS regardless of insertion order.
export const getOrderedSpots = (spots, spotOrder) => {
  if (!spotOrder || !spotOrder.length) {
    return Object.values(spots || {});
  }
  return spotOrder.map((id) => spots[id]).filter(Boolean);
};

// Helper function to format date/time
export const formatDateTime = (timeString) => {
    const date = new Date(timeString);
    return {
        date: date.toLocaleDateString('en-US', { 
            weekday: 'long', 
            month: 'long', 
            day: 'numeric',
            year: 'numeric'
        }),
        time: date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit' 
        }),
        hour: date.getHours().toString().padStart(2, '0') + ':' + date.getMinutes().toString().padStart(2, '0'),
        dayOfMonth: date.getDate(),
        shortDayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        iso: date.toISOString().split('T')[0] // YYYY-MM-DD format
    };
};

// Helper function to get wave direction text
export const getDirectionText = (degrees) => {
    if (!degrees && degrees !== 0) return 'N/A';
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
};

// Angular difference (0-180), accounting for wrap-around at 360 degrees
const wrappedDirectionDiff = (a, b) => {
    let diff = Math.abs(a - b);
    return diff > 180 ? 360 - diff : diff;
};

// Compares an actual direction against one or more ideal directions (supports the
// legacy single-value format as well as the current multiselect array) and returns
// the closest match, or null if there's nothing to compare against.
export const getClosestDirectionDiff = (actualDegrees, idealDirections) => {
    if (actualDegrees === null || actualDegrees === undefined) return null;

    const idealList = Array.isArray(idealDirections)
        ? idealDirections
        : (idealDirections !== null && idealDirections !== undefined ? [idealDirections] : []);

    const validIdeals = idealList
        .filter(deg => deg !== null && deg !== undefined && !Number.isNaN(Number(deg)))
        .map(Number);

    if (validIdeals.length === 0) return null;

    const actual = Number(actualDegrees);

    return validIdeals.reduce((closest, ideal) => {
        const diff = wrappedDirectionDiff(actual, ideal);
        return (!closest || diff < closest.diff) ? { diff, closest: ideal } : closest;
    }, null);
};



// SVG arrow that rotates by the exact degrees value (0 = North/up)
export const DirectionArrowSVG = ({ degrees = 0, size = 16, color = '#60a5fa' }) => {
    if (degrees === null || degrees === undefined || Number.isNaN(Number(degrees))) return null;
    const d = ((Number(degrees) + 180) % 360 + 360) % 360; // normalize 0..359 and flip direction
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" className="inline-block align-middle" aria-hidden="true" role="img">
            <g transform={`rotate(${d} 12 12)`}>
                <path
                    d="m18.787 9.473s-4.505-4.502-6.259-6.255c-.147-.146-.339-.22-.53-.22-.192 0-.384.074-.531.22-1.753 1.753-6.256 6.252-6.256 6.252-.147.147-.219.339-.217.532.001.19.075.38.221.525.292.293.766.295 1.056.004l4.977-4.976v14.692c0 .414.336.75.75.75.413 0 .75-.336.75-.75v-14.692l4.978 4.978c.289.29.762.287 1.055-.006.145-.145.219-.335.221-.525.002-.192-.07-.384-.215-.529z"
                    clipRule="evenodd"
                    fillRule="evenodd"
                    strokeLinejoin="round"
                    strokeMiterlimit="2"
                    fill={color}
                />
            </g>
        </svg>
    );
};

export const getWavePeriodClass = (period) => {
    if (!period) return '';
    if (period < 5) return 'text-blue-300';        // Very poor
    if (period < 8) return 'text-green-300';     // Poor
    if (period < 10) return 'text-yellow-300';    // Fair
    if (period < 13) return 'text-orange-300';     // Good
    return 'text-red-300';                       // Excellent
};

// Helper function to format wave height with color coding
export const getWaveHeightClass = (height) => {
    if (!height) return '';
    if (height < 1) return 'text-blue-300';
    if (height < 2) return 'text-green-300 ';
    if (height < 3) return 'text-yellow-300 ';
    if (height < 4) return 'text-orange-300 ';
    if (height < 5) return 'text-red-300 ';
    return 'text-purple-300 ';
};

// Helper function to get wave condition visual indicator
export const getWaveIndicator = (height) => {
    if (!height) return '';
    if (height < 1) return <div className="w-2 h-2 bg-blue-400 rounded-full"></div>;
    if (height < 2) return <div className="w-2 h-4 bg-green-400 rounded-sm"></div>;
    if (height < 3) return <div className="w-3 h-6 bg-yellow-400 rounded-sm"></div>;
    if (height < 4) return <div className="w-3 h-8 bg-orange-400 rounded-sm"></div>;
    if (height < 5) return <div className="w-4 h-10 bg-red-400 rounded-sm"></div>;
    return <div className="w-4 h-12 bg-purple-400 rounded-sm"></div>;
};

// Helper function for wind speed classes
export const getWindSpeedClass = (speed) => {
    if (!speed) return '';
    if (speed < 10) return 'text-green-300 ';
    if (speed < 20) return 'text-yellow-300 ';
    if (speed < 30) return 'text-orange-300 ';
    return 'text-red-300 ';
};

// Helper function to get wind condition visual indicator
export const getWindIndicator = (speed) => {
    if (!speed) return null;
    const intensity = speed < 10 ? 1 : speed < 20 ? 2 : speed < 30 ? 3 : 4;
    return (
        <div className="flex gap-0.5">
            {[...Array(intensity)].map((_, i) => (
                <div key={i} className={`w-[3px] h-3 ${speed < 10 ? 'bg-green-400' : speed < 20 ? 'bg-yellow-400' : speed < 30 ? 'bg-orange-400' : 'bg-red-400'} rounded-sm`}></div>
            ))}
        </div>
    );
};

// Helper function for temperature classes
export const getTempClass = (temp) => {
    if (!temp) return '';
    if (temp < 15) return 'text-blue-300 ';
    if (temp < 20) return 'text-cyan-300 ';
    if (temp < 25) return 'text-yellow-300 ';
    return 'text-red-300 ';
};

// Helper function to get temperature visual indicator
export const getTempIndicator = (temp) => {
    if (!temp) return null;
    if (temp < 15) return <div className="w-3 h-3 bg-blue-400 rounded-full border-2 border-blue-300"></div>;
    if (temp < 20) return <div className="w-3 h-3 bg-cyan-400 rounded-full border-2 border-cyan-300"></div>;
    if (temp < 25) return <div className="w-3 h-3 bg-yellow-400 rounded-full border-2 border-yellow-300"></div>;
    return <div className="w-3 h-3 bg-red-400 rounded-full border-2 border-red-300"></div>;
};

// Helper function for rain classes
export const getRainClass = (rain) => {
    if (rain === null || rain === undefined) return '';
    if (rain < 1) return 'text-blue-300';
    if (rain < 5) return 'text-green-300';
    if (rain < 10) return 'text-yellow-300';
    if (rain < 20) return 'text-orange-300';
    return 'text-red-300';
};

// Helper function to get rain visual indicator
export const getRainIndicator = (rain) => {
    if (rain === null || rain === undefined) return null;
    if (rain < 1) return <div className="w-2 h-2 bg-blue-400 rounded-full"></div>;
    if (rain < 5) return <div className="w-2 h-4 bg-green-400 rounded-sm"></div>;
    if (rain < 10) return <div className="w-3 h-6 bg-yellow-400 rounded-sm"></div>;
    if (rain < 20) return <div className="w-3 h-8 bg-orange-400 rounded-sm"></div>;
    return <div className="w-4 h-10 bg-red-400 rounded-sm"></div>;
};